import React, { useState, useContext, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import { AlertCircle, X, ChevronDown, Plus, Search, MapPin, Sofa, BedDouble, Utensils, UtensilsCrossed, Bath, Baby, Users, LayoutGrid, Briefcase, GraduationCap, Stethoscope, Warehouse, MoreHorizontal } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
// import InteractiveMap from '../../components/InteractiveMap';
import SuccessModal from '../../components/modals/SuccessModal';
import { DevicesContext } from '../../contexts/DevicesContext';
import { UserManagementContext } from '../../contexts/UserManagementContext';
import { useAuth } from '../../hooks/useAuth';
import '../../styles/pages/manually-add-device.css';
import '../../styles/components/InteractiveMap.css';
import '../../styles/components/settings.css';
import ArcGISMap from '../../components/ArcGISMap';
import { useParams } from 'react-router-dom';
import { UpdateDeviceRequest, deviceApi, DeviceType, Device } from '../../services/deviceApi';
import {
  extractModelLetterFromSerial,
  matchDeviceTypeByModelLetter,
  parseSerialNumber,
  validateSerialNumber
} from '../../config/deviceModels';
// import DeviceConfigFlow from './DeviceConfigFlow'
import DeviceConfigFlowMobile from './DeviceConfigFlowMobile'
import DeviceConfigFlowWeb from './configFlowWeb/DeviceConfigFlowWeb'
import { isRNApp } from '../../utils/helperFunctions'
// import { EnvironmentModes } from '../../utils/helperFunctions';

// Maps each predefined installation-location option to a representative icon
const LOCATION_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  'Living Room': Sofa,
  'Bedroom': BedDouble,
  'Kitchen': Utensils,
  'Dining Room': UtensilsCrossed,
  'Bathroom': Bath,
  "Kid's Room": Baby,
  'Conference Room': Users,
  'Open Space': LayoutGrid,
  'Office Space': Briefcase,
  'School Classroom': GraduationCap,
  'Hospital Room': Stethoscope,
  'Warehouse Space': Warehouse,
  'Other': MoreHorizontal,
};

// Validation schema using yup
const deviceSchema = yup.object().shape({
  DeviceTypeId: yup
    .number()
    .required('Device type is required')
    .test('not-empty', 'Please select a device type', value => value !== 0),
  DeviceName: yup
    .string()
    .required('Device name is required')
    .test(
      'min-length',
      'Device name must be between 4 and 30 characters (alphanumeric)',
      value => !value || value.length >= 4
    )
    .test(
      'max-length',
      'Device name must not exceed 30 characters',
      value => !value || value.length <= 30
    )
    .matches(
      /^[a-zA-Z0-9\s]+$/,
      'Device name can only contain letters, numbers, and spaces'
    )
    .test(
      'not-only-spaces',
      'Device name cannot be only spaces',
      value => !value || value.trim().length > 0
    )
    .test(
      'no-leading-trailing-space',
      'Device name cannot start or end with a space',
      value => !value || value === value.trim()
    )
    .test(
      'no-multi-spaces',
      'Device name cannot contain consecutive spaces',
      value => !value || !/\s{2,}/.test(value)
    ),
  SerialNumber: yup
    .string()
    .required('Serial number is required')
    .test('smarteco-format', function (value) {
      if (!value) return true; // `required` handles empty case
      const result = validateSerialNumber(value);
      if (result.valid) return true;
      return this.createError({ message: result.error || 'Invalid serial number.' });
    }),
  DeviceLocationType: yup
    .string()
    .required('Installation type is required')
    .oneOf(['indoor', 'outdoor'], 'Please select a valid installation type'),
  InstallationLocation: yup
    .string()
    .required('Installation location is required')
    .min(3, 'Installation location must be at least 3 characters')
    .max(50, 'Installation location must be less than 50 characters'),
  // Sector: yup
  //   .string()
  //   .required('Sector or group is required')
  //   .test('not-empty', 'Please select or enter a sector/group', value => value !== ''),
  Description: yup
    .string()
    .test(
      'not-only-spaces',
      'Description cannot be only spaces',
      value => !value || value.trim().length > 0
    )
    .max(200, 'No more characters allowed. Maximum 200 characters.'),
  Latitude: yup
    .number()
    .nullable()
    .notRequired(),
  Longitude: yup
    .number()
    .nullable()
    .notRequired(),
  DeviceImage: yup
    .mixed()
    .nullable()
    .test(
      'file-size',
      'Image file size must be less than 5MB',
      function (value) {
        if (!value) return true; // Optional field
        const file = value as File;
        return file.size <= 5 * 1024 * 1024; // 5MB limit
      }
    )
    .test('file-type', 'Only image files are allowed', function (value) {
      if (!value) return true; // Optional field
      const file = value as File;
      return ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'].includes(
        file.type
      );
    }),
  SiteId: yup
    .number()
    .nullable()
    .required('Please select a site')
    .test('not-empty', 'Please select a site', value => value !== null && value !== undefined && value != 0),
});

interface DeviceFormData {
  DeviceTypeId: number;
  DeviceName: string;
  SerialNumber: string;
  DeviceModel: string;
  DeviceLocationType: 'indoor' | 'outdoor';
  InstallationLocation: string;
  // Sector: string;
  Description: string;
  // IsActive: boolean;
  Latitude: number | null;
  Longitude: number | null;
  DeviceImage: File | null;
  DeviceImageUrl: string | null;
  // EnvironmentMode: string | null;
  SiteId: number | null;
}

const ManuallyAddDevicePage: React.FC = () => {
  const devicesContext = useContext(DevicesContext);
  if (!devicesContext) {
    throw new Error('useDevices must be used within a DevicesProvider');
  }
  const { createDevice, devices, getUserDevices, updateDevice } =
    devicesContext;
  const userMgmtContext = useContext(UserManagementContext);
  const { sites = [], getSites, createSite } = userMgmtContext || {};
  const [showSiteModal, setShowSiteModal] = useState(false);
  const [showAddSiteForm, setShowAddSiteForm] = useState(false);
  const [newSiteName, setNewSiteName] = useState('');
  const [newSiteLocation, setNewSiteLocation] = useState('');
  const { user } = useAuth();
  const canManageSites = user?.role === 'Super Admin' || user?.role === 'Admin';
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState<DeviceFormData>({
    DeviceTypeId: 0,
    DeviceName: '',
    SerialNumber: '',
    DeviceModel: '',
    DeviceLocationType: 'indoor',
    InstallationLocation: '',
    // Sector: '',
    Description: '',
    // IsActive: true,
    Latitude: null,
    Longitude: null,
    DeviceImage: null,
    DeviceImageUrl: null,
    SiteId: null,
    // EnvironmentMode: null,
  });

  const { id } = useParams();

  useEffect(() => {
    if (id && devices.length <= 0) {
      getUserDevices();
    }
  }, []);

  useEffect(() => { getSites?.(); }, []);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 860);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 860);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Location name dropdown state
  const [showCustomLocationInput, setShowCustomLocationInput] = useState(false);
  const [selectedLocationType, setSelectedLocationType] = useState<string>('');
  const [showLocationModal, setShowLocationModal] = useState(false);
  // "Other Details" section is collapsed (minimized) by default
  const [showOtherDetails, setShowOtherDetails] = useState(false);
  // Mobile location search (replaces the map on mobile view)
  const [locationQuery, setLocationQuery] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState<{ text: string; magicKey: string }[]>([]);
  const [isLocating, setIsLocating] = useState(false);

  // const [wifiname, setWifiName] = useState('');
  // const [wifipassword, setWifiPassword]=useState('');
  // const [isDeviceConnectedFromNetwork,setIsDeviceConnectedFromNetwork] = useState(false);
  
  // QR-scan handoff travels through BOTH location.state (preferred) and URL query params
  // (resilient fallback — survives refresh, StrictMode double-mount, and any browser/router
  // edge case that drops `history.state`). Merge the two, preferring state when it exists.
  const [searchParams] = useSearchParams();
  const rawStateRef = useRef(location.state as {
    preFilledSerialNumber?: string;
    preFilledDeviceType?: string;
    preFilledDeviceTypeId?: number;
    preFilledDeviceNumber?: string;
    preFilledDeviceName?: string;
  } | null);
  const scannedState = useMemo(() => {
    const fromState = rawStateRef.current;
    const sn = fromState?.preFilledSerialNumber ?? searchParams.get('sn') ?? undefined;
    if (!sn) return null;
    const dtIdRaw = fromState?.preFilledDeviceTypeId ?? searchParams.get('dtId');
    const dtId = dtIdRaw != null && dtIdRaw !== '' ? Number(dtIdRaw) : undefined;
    return {
      preFilledSerialNumber: sn,
      preFilledDeviceType: fromState?.preFilledDeviceType ?? searchParams.get('dt') ?? undefined,
      preFilledDeviceTypeId: Number.isFinite(dtId) ? dtId : undefined,
      preFilledDeviceNumber: fromState?.preFilledDeviceNumber ?? searchParams.get('dn') ?? undefined,
      preFilledDeviceName: fromState?.preFilledDeviceName ?? searchParams.get('name') ?? undefined,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Track if device came from QR scan to show config flow after save
  const cameFromQRScan = !!scannedState?.preFilledSerialNumber;

  // Skip device config flow when coming from QR scan (has preFilledSerialNumber)
  // This allows mobile users to go directly to Basic Details form after scanning
  const [showConfigFlow, setShowConfigFlow]=useState(false);

  // Track if device was successfully saved to show config flow
  const [deviceSavedSuccessfully, setDeviceSavedSuccessfully] = useState(false);
  const [configFlowWasShown, setConfigFlowWasShown] = useState(false);
  
  // Track if device exists (fetched from API)
  const [deviceExists, setDeviceExists] = useState(false);
  // Store the existing device ID when found via QR scan
  const [existingDeviceId, setExistingDeviceId] = useState<number | null>(null);

  // Check for existing device when QR code is scanned
  useEffect(() => {
    let isMounted = true;
    
    const checkExistingDevice = async () => {
      if (cameFromQRScan && !id && scannedState?.preFilledSerialNumber) {
        try {
          // Fetch device by serial number
          const serialNumber = scannedState.preFilledSerialNumber.trim();
          const existingDevice = await deviceApi.getDeviceBySerialNumber(serialNumber);
          
          if (!isMounted) return;

          if (existingDevice) {
            // Check if device belongs to another user or if conditions are met for update
            if (user) {
              try {
                const currentUserId = user.userId;
                
                // Check if device has userId field (may be in snake_case or camelCase)
                const deviceUserId = (existingDevice as any).userId || (existingDevice as any).user_id || (existingDevice as any).UserId;
                
                // Get onboardingStatus and operationalStatus (check both snake_case and camelCase)
                const onboardingStatus = (existingDevice as any).onboarding_status || existingDevice.onboardingStatus;
                const operationalStatus = (existingDevice as any).operational_status || existingDevice.operationalStatus;
                
                // If device has a userId and it doesn't match current user, show error
                if (deviceUserId !== undefined && deviceUserId !== null && deviceUserId !== currentUserId) {
                  setSuccessMessage('This Device is mapped to other user please check with Admin department for further support');
                  setSuccessStatus('failed');
                  setShowSuccessModal(true);
                  setModalActionText('OK');
                  setModalOnAction(() => {
                    return () => {
                      setShowSuccessModal(false);
                      setModalActionText(undefined);
                      setModalOnAction(undefined);
                      navigate('/devices');
                    };
                  });
                  return; // Don't proceed
                }
                
                // Check if device has userId null, onboardingStatus is "UNASSIGNED", and operationalStatus is "FACTORY_RESET"
                const hasNullUserId = deviceUserId === null || deviceUserId === undefined;
                const isUnassigned = onboardingStatus && onboardingStatus.toUpperCase() === 'UNASSIGNED';
                const isFactoryReset = operationalStatus && operationalStatus.toUpperCase() === 'FACTORY_RESET';
                
                // If device matches all conditions, mark for update
                if (hasNullUserId && isUnassigned && isFactoryReset) {
                  // Set deviceExists to true so updateDevice is called with userId and status updates
                  setDeviceExists(true);
                  setExistingDeviceId(existingDevice.deviceId);
                  setWifiConnected(false);
                  setWifiNetworkName(null);
                  return; // Allow user to proceed - will update device with userId and status
                }
              } catch (error) {
                console.error('Error parsing user data:', error);
              }
            }

            // For QR scan: treat as fresh new device (no pre-filling, use createDevice)
            // Don't set deviceExists - this will allow createDevice to be called
            setDeviceExists(false);
            setExistingDeviceId(null);
            setWifiConnected(false);
            setWifiNetworkName(null);
            return; // Allow user to proceed with adding as a new device
          } else {
            // New device - WiFi not configured
            setDeviceExists(false);
            setExistingDeviceId(null);
            setWifiConnected(false);
            setWifiNetworkName(null);
          }
        } catch (error: any) {
          if (!isMounted) return;
          // Check if error indicates device belongs to another user
          const errorMessage = error?.message || '';
          if (errorMessage.includes('mapped to other user') ||
              errorMessage.includes('belongs to another user') ||
              errorMessage.includes('other user') ||
              errorMessage.includes('unauthorized') ||
              errorMessage.includes('forbidden') ||
              (error?.response?.status === 403) ||
              (error?.response?.status === 401)) {
            // Device belongs to another user
            setSuccessMessage('This Device is mapped to other user please check with Admin department for further support');
            setSuccessStatus('failed');
            setShowSuccessModal(true);
            setModalActionText('OK');
            setModalOnAction(() => {
              return () => {
                setShowSuccessModal(false);
                setModalActionText(undefined);
                setModalOnAction(undefined);
                navigate('/devices');
              };
            });
            return;
          }
          
          // Device not found or other error - continue with normal flow
          // This is expected for new devices, so we don't show an error
          if (error?.message && !error.message.includes('404') && !error.message.includes('Not Found')) {
            console.error('Error fetching device by serial number:', error);
          }
          // New device - WiFi not configured
          setDeviceExists(false);
          setExistingDeviceId(null);
          setWifiConnected(false);
          setWifiNetworkName(null);
        }
      }
    };

    checkExistingDevice();
    
    return () => {
      isMounted = false;
    };
  }, [cameFromQRScan, id, scannedState?.preFilledSerialNumber, navigate]);
  
  // Navigate to devices page when config flow is closed after successful device save
  useEffect(() => {
    // Only navigate if config flow was shown AND is now closed
    if (deviceSavedSuccessfully && configFlowWasShown && !showConfigFlow) {
      console.log('→ Config flow closed, navigating to /devices');
      navigate('/devices');
    }
  }, [showConfigFlow, deviceSavedSuccessfully, configFlowWasShown, navigate]);
  
  // Track when config flow is shown
  useEffect(() => {
    if (showConfigFlow) {
      console.log('✓ Config flow is now showing');
      setConfigFlowWasShown(true);
    }
  }, [showConfigFlow]);

  

  const locationOptions = [
    'Living Room',
    'Bedroom',
    'Kitchen',
    'Dining Room',
    'Bathroom',
    'Kid\'s Room',
    'Conference Room',
    'Open Space',
    'Office Space',
    'School Classroom',
    'Hospital Room',
    'Warehouse Space',
    'Other',
  ];

  // Reusable function to pre-fill form data from device object
  const prefillDeviceData = (device: Device) => {
    // Extract first 7 digits of serial number for Device Model
    const serialNumber = device.serialNumber || '';
    const deviceModel = serialNumber.length >= 7 ? serialNumber.substring(0, 7) : serialNumber;
    
    // Pre-fill form with device data
    setFormData(prev => ({
      ...prev,
      DeviceTypeId: device.deviceTypeId || prev.DeviceTypeId,
      DeviceName: device.deviceName || prev.DeviceName,
      SerialNumber: device.serialNumber || prev.SerialNumber,
      DeviceModel: deviceModel,
      DeviceLocationType: (() => {
        const raw = device.deviceLocationType || (device as any).device_location_type || '';
        const val = raw.toLowerCase();
        return val === 'indoor' || val === 'outdoor' ? val as 'indoor' | 'outdoor' : 'indoor';
      })(),
      InstallationLocation: device.installationLocation || prev.InstallationLocation,
      Description: device.description || prev.Description,
      Latitude: device.latitude ?? prev.Latitude,
      Longitude: device.longitude ?? prev.Longitude,
      DeviceImageUrl: device.deviceImageUrl || prev.DeviceImageUrl,
      SiteId: device.siteId || prev.SiteId
    }));

    // Initialize location type dropdown
    const installationLocation = device.installationLocation?.trim() || '';
    if (installationLocation) {
      const predefinedLocations = locationOptions.filter(opt => opt !== 'Other');
      const isPredefinedLocation = predefinedLocations.some(
        opt => opt.toLowerCase() === installationLocation.toLowerCase()
      );

      if (isPredefinedLocation) {
        const matchedOption = predefinedLocations.find(
          opt => opt.toLowerCase() === installationLocation.toLowerCase()
        );
        setSelectedLocationType(matchedOption || installationLocation);
        setShowCustomLocationInput(false);
      } else {
        setSelectedLocationType('Other');
        setShowCustomLocationInput(true);
      }
    } else {
      // No location set - reset to default
      setSelectedLocationType('');
      setShowCustomLocationInput(false);
    }
  };

  const DEVICE_TYPES_CACHE_KEY = 'deviceTypesCache';

  const fetchDeviceTypes = async () => {
    try {
      setDeviceTypesLoading(true);
      setDeviceTypesError(null);
      const types = await deviceApi.getDeviceTypes();
      setDeviceTypes(types);
      localStorage.setItem(DEVICE_TYPES_CACHE_KEY, JSON.stringify(types));
    } catch (error) {
      const cachedTypesRaw = localStorage.getItem(DEVICE_TYPES_CACHE_KEY);
      if (cachedTypesRaw) {
        try {
          const cachedTypes: DeviceType[] = JSON.parse(cachedTypesRaw);
          if (Array.isArray(cachedTypes) && cachedTypes.length > 0) {
            setDeviceTypes(cachedTypes);
            setDeviceTypesError(null);
            return;
          }
        } catch {
          // Ignore cache parsing errors
        }
      }

      // Fallback to minimal list using scanned data if available
      if (
        scannedState?.preFilledDeviceTypeId &&
        scannedState?.preFilledDeviceType
      ) {
        setDeviceTypes([
          {
            id: scannedState.preFilledDeviceTypeId,
            typeName: scannedState.preFilledDeviceType,
            description: scannedState.preFilledDeviceType,
          },
        ]);
        setDeviceTypesError(null);
        return;
      }

      setDeviceTypesError('Failed to load device types. Please try again.');
    } finally {
      setDeviceTypesLoading(false);
    }
  };
  // Fetch device types on component mount
  useEffect(() => {
    fetchDeviceTypes();
  }, []);

  useEffect(() => {
    if (id && devices.length > 0) {
      const device = devices.find(device => device.deviceId === Number(id));
      if (device) {
        // Pre-fill form with device data
        prefillDeviceData(device);
        
        // Set DeviceImage to null for edit mode
        setFormData(prev => ({
          ...prev,
          DeviceImage: null,
        }));

        // Always fetch device details when editing to get the most up-to-date onboarding_status
        // Use serial number instead of device ID
        const fetchDeviceDetails = async () => {
          try {
            // Mark device as registered when editing (device already exists)
            setIsDeviceRegistered(true);
            
            // Use serial number from device or formData as fallback
            const serialNumber = device.serialNumber || formData.SerialNumber;
            if (serialNumber && serialNumber.trim() !== '') {
              console.log('Fetching device details by serial number:', serialNumber);
              const deviceDetails = await deviceApi.getDeviceBySerialNumber(serialNumber.trim());
              const deviceOnboardingStatus = (deviceDetails as any).onboarding_status || deviceDetails.onboardingStatus;
              if (deviceOnboardingStatus !== undefined && deviceOnboardingStatus !== null && deviceOnboardingStatus !== '') {
                // If onboarding_status is 'REGISTERED', WiFi is not connected (red)
                // Otherwise (e.g., 'CONNECTED', 'ONLINE', etc.), WiFi is connected (green)
                const isWifiConnected = deviceOnboardingStatus?.toUpperCase() !== 'REGISTERED';
                setWifiConnected(isWifiConnected);
                
                // Get WiFi network name from API response
                const networkName = deviceDetails?.wifiNetworkName;
                
                if (isWifiConnected && networkName) {
                  setWifiNetworkName(networkName);
                } else if (isWifiConnected) {
                  setWifiNetworkName(null); // Connected but network name not available
                } else {
                  setWifiNetworkName(null); // Not connected (REGISTERED status)
                }
                
                console.log('Device onboarding status (from API by serial):', deviceOnboardingStatus, 'WiFi Connected:', isWifiConnected, 'Network Name:', networkName);
              } else {
                // Default to not connected if status is not available
                setWifiConnected(false);
                setWifiNetworkName(null);
                console.log('Device onboarding status not available, defaulting to not connected');
              }
            } else {
              // No serial number available
              setWifiConnected(false);
              setWifiNetworkName(null);
              console.log('Serial number not available for WiFi status check');
            }
          } catch (error) {
            console.error('Error fetching device details for WiFi status by serial number:', error);
            // Default to not connected on error
            setWifiConnected(false);
            setWifiNetworkName(null);
          }
        };
        fetchDeviceDetails();
      }
    }
  }, [id, devices]);

  // Add validation error states
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [successStatus, setSuccessStatus] = useState<'success' | 'failed'>(
    'success'
  );
  const [modalActionText, setModalActionText] = useState<string | undefined>(undefined);
  const [modalOnAction, setModalOnAction] = useState<(() => void) | undefined>(undefined);
  
  // WiFi connection status (null = unknown, true = connected, false = not configured)
  const [wifiConnected, setWifiConnected] = useState<boolean | null>(null);
  // WiFi network name (SSID)
  const [wifiNetworkName, setWifiNetworkName] = useState<string | null>(null);
  // Track if device is registered (saved successfully)
  const [isDeviceRegistered, setIsDeviceRegistered] = useState(false);

  // Device types state
  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
  const [deviceTypesLoading, setDeviceTypesLoading] = useState(true);
  const [deviceTypesError, setDeviceTypesError] = useState<string | null>(null);

  // Always pre-fill serial number and device name (before device types load)
  useEffect(() => {
    if (scannedState && !id) {
      const serialNumber = scannedState.preFilledSerialNumber || '';
      const deviceModel = serialNumber.length >= 7 ? serialNumber.substring(0, 7) : serialNumber;
      
      setFormData(prev => ({
        ...prev,
        SerialNumber: serialNumber || prev.SerialNumber,
        DeviceName: scannedState.preFilledDeviceName || prev.DeviceName,
        DeviceModel: deviceModel || prev.DeviceModel,
      }));
      
      // Trigger auto-fill for device type and name when serial number is set
      // This ensures auto-fill happens even if deviceTypes haven't loaded yet
      if (scannedState.preFilledSerialNumber && deviceTypes.length > 0) {
        const serialNumber = scannedState.preFilledSerialNumber.trim().toUpperCase();
        const parsedSerial = parseSerialNumber(serialNumber);
        
        if (parsedSerial) {
          const modelLetter = parsedSerial.modelLetter;
          const deviceId = parsedSerial.deviceId;
          const matchedType = matchDeviceTypeByModelLetter(modelLetter, deviceTypes);
          
          if (matchedType) {
            setFormData(prev => {
              // Only update if fields are empty to avoid overwriting user input
              const updates: Partial<DeviceFormData> = {};
              if (!prev.DeviceTypeId || prev.DeviceTypeId === 0) {
                updates.DeviceTypeId = matchedType.id;
              }
              if (!prev.DeviceName || prev.DeviceName.trim() === '') {
                updates.DeviceName = `${matchedType.description} ${deviceId}`;
              }
              return { ...prev, ...updates };
            });
          }
        }
      }
    }
  }, [scannedState, id, deviceTypes]);

  // Handle device type after device types are loaded
  useEffect(() => {
    if (!scannedState || id || deviceTypes.length === 0) return;

    setFormData(prev => {
      const updates: Partial<DeviceFormData> = {};
      const deviceNameEmpty = !prev.DeviceName || prev.DeviceName.trim() === '';

      if (scannedState.preFilledSerialNumber) {
        const serialNumber = scannedState.preFilledSerialNumber;
        updates.SerialNumber = serialNumber;
        updates.DeviceModel = serialNumber.length >= 7 ? serialNumber.substring(0, 7) : serialNumber;

        const parsedSerial = parseSerialNumber(serialNumber);
        if (parsedSerial) {
          const matchedType = matchDeviceTypeByModelLetter(parsedSerial.modelLetter, deviceTypes);
          if (matchedType) {
            updates.DeviceTypeId = matchedType.id;
            if (deviceNameEmpty) {
              updates.DeviceName = `${matchedType.description} ${parsedSerial.deviceId}`;
            }
          }
        }
      }

      if (!updates.DeviceName && scannedState.preFilledDeviceName && deviceNameEmpty) {
        updates.DeviceName = scannedState.preFilledDeviceName;
      }

      if (!updates.DeviceTypeId) {
        if (scannedState.preFilledDeviceTypeId) {
          const typeExists = deviceTypes.find(type => type.id === scannedState.preFilledDeviceTypeId);
          if (typeExists) {
            updates.DeviceTypeId = scannedState.preFilledDeviceTypeId;
          }
        } else if (scannedState.preFilledDeviceType) {
          const matchedType = matchDeviceTypeByModelLetter(scannedState.preFilledDeviceType, deviceTypes);
          if (matchedType) {
            updates.DeviceTypeId = matchedType.id;
          }
        }
      }

      return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
    });
  }, [scannedState, id, deviceTypes]);

  // const sectorOptions = [
  //   'Commercial',
  //   'Industrial',
  //   'Residential',
  //   'Agricultural',
  //   'Healthcare',
  //   'Educational',
  // ];

  const handleInputChange = async (
    field: keyof DeviceFormData,
    value: string | boolean
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Auto-detect device type and device name based on serial number
    if (field === 'SerialNumber' && typeof value === 'string') {
      const serialNumber = value.trim().toUpperCase();

      // Extract first 7 digits for Device Model
      const deviceModel = serialNumber.length >= 7 ? serialNumber.substring(0, 7) : serialNumber;
      setFormData(prev => ({
        ...prev,
        DeviceModel: deviceModel,
      }));

      // If serial number is cleared, reset device type and name
      if (!serialNumber || serialNumber.length === 0) {
        setFormData(prev => ({...prev, DeviceTypeId: 0, DeviceName: '', DeviceModel: ''}));
      } else {
        // Parse the serial number to extract components
        const parsedSerial = parseSerialNumber(serialNumber);
        
        if (parsedSerial) {
          // Extract model letter from serial (e.g., "C" from "SEI100C784250001")
          const modelLetter = parsedSerial.modelLetter;
          
          // Match model letter with API device types dynamically
          const matchedType = matchDeviceTypeByModelLetter(modelLetter, deviceTypes);
          
          if (matchedType) {
            // Generate device name: DeviceType DeviceID
            // Example: "CORE 0001" or "MINI 0002"
            const autoGeneratedName = `${matchedType.description} ${parsedSerial.deviceId}`;
            
            // Set both device type ID and auto-generated device name
            setFormData(prev => ({
              ...prev, 
              DeviceTypeId: matchedType.id,
              DeviceName: autoGeneratedName
            }));
          }
        } else {
          // If parsing fails, try simple model letter extraction
          const modelLetter = extractModelLetterFromSerial(serialNumber);
          
          if (modelLetter) {
            const matchedType = matchDeviceTypeByModelLetter(modelLetter, deviceTypes);
            
            if (matchedType) {
              // Set device type only (can't generate name without deviceId)
              setFormData(prev => ({...prev, DeviceTypeId: matchedType.id}));
            }
          }
        }
      }
    }

    try {
      await deviceSchema.validateAt(field, { [field]: value });
      // Clear field error when field validation passes
      if (fieldErrors[field]) {
        setFieldErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    } catch (validationError) {
      setFieldErrors(prev => ({
        ...prev,
        [field]: (validationError as Error).message,
      }));
    }
  };

  const handleDeviceLocationTypeChange = (type: 'indoor' | 'outdoor') => {
    setFormData(prev => ({
      ...prev,
      DeviceLocationType: type,
    }));

    // Clear installation type error
    if (fieldErrors.DeviceLocationType) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.DeviceLocationType;
        return newErrors;
      });
    }
  };

  // const handleSectorChange = (sector: string) => {
  //   if (sector === 'custom') {
  //     setShowCustomSectorInput(true);
  //     setFormData(prev => ({
  //       ...prev,
  //       Sector: '',
  //     }));
  //     setCustomSector('');

  //     // Focus the custom sector input after it appears
  //     setTimeout(() => {
  //       const customInput = document.querySelector('.add-device-custom-sector-input input') as HTMLInputElement;
  //       if (customInput) {
  //         customInput.focus();
  //       }
  //     }, 100);
  //   } else {
  //     setShowCustomSectorInput(false);
  //     setFormData(prev => ({
  //       ...prev,
  //       Sector: sector,
  //     }));
  //     setCustomSector('');
  //   }

  //   // Clear sector error
  //   if (fieldErrors.Sector) {
  //     setFieldErrors(prev => {
  //       const newErrors = { ...prev };
  //       delete newErrors.Sector;
  //       return newErrors;
  //     });
  //   }
  // };

  // const handleCustomSectorSubmit = (e?: React.FormEvent) => {
  //   // Always prevent form submission to avoid page refresh
  //   if (e) {
  //     e.preventDefault();
  //     e.stopPropagation();
  //   }

  //   // Check if custom sector is empty
  //   if (!customSector.trim()) {
  //     setFieldErrors(prev => ({
  //       ...prev,
  //       Sector: 'Please enter a custom sector'
  //     }));
  //     return;
  //   }

  //   // Check if custom sector is too short
  //   if (customSector.trim().length < 3) {
  //     setFieldErrors(prev => ({
  //       ...prev,
  //       Sector: 'Custom sector must be at least 3 characters'
  //     }));
  //     return;
  //   }

  //   // If validation passes, add the custom sector
  //   setFormData(prev => ({
  //     ...prev,
  //     Sector: customSector.trim(),
  //   }));
  //   setShowCustomSectorInput(false);
  //   setCustomSector('');

  //   // Clear sector error
  //   if (fieldErrors.Sector) {
  //     setFieldErrors(prev => {
  //       const newErrors = { ...prev };
  //       delete newErrors.Sector;
  //       return newErrors;
  //     });
  //   }
  // };

  const validateForm = async (): Promise<boolean> => {
    try {
      // Viewers and individual users can't manage sites, so skip SiteId validation for them
      const schema = canManageSites ? deviceSchema : deviceSchema.omit(['SiteId']);
      await schema.validate(formData, { abortEarly: false });
      setFieldErrors({});
      return true;
    } catch (error: any) {
      if (error.inner) {
        const errors: { [key: string]: string } = {};
        error.inner.forEach((err: any) => {
          errors[err.path] = err.message;
        });
        setFieldErrors(errors);
      }
      return false;
    }
  };

  const CheckDeviceDetailsChanged = (): boolean => {
    const device = devices.find(device => device.deviceId === Number(id));
    const serialNumber = device?.serialNumber || '';
    const deviceModel = serialNumber.length >= 7 ? serialNumber.substring(0, 7) : serialNumber;
    
    const deviceData: DeviceFormData = {
      DeviceTypeId: device?.deviceTypeId || 0,
      DeviceName: device?.deviceName || '',
      SerialNumber: serialNumber,
      DeviceModel: deviceModel,
      DeviceLocationType: (() => {
        const raw = device?.deviceLocationType || (device as any)?.device_location_type || '';
        const val = raw.toLowerCase();
        return val === 'indoor' || val === 'outdoor' ? val as 'indoor' | 'outdoor' : 'indoor';
      })(),
      InstallationLocation: device?.installationLocation || '',
      Description: device?.description || '',
      Latitude: device?.latitude ?? null,
      Longitude: device?.longitude ?? null,
      DeviceImage: null,
      DeviceImageUrl: device?.deviceImageUrl || null,
      SiteId: device?.siteId ?? null,
    };

    // Compare field-by-field; ignore DeviceImage (always null in deviceData)
    return (Object.keys(deviceData) as Array<keyof DeviceFormData>).some(
      key => key !== 'DeviceImage' && deviceData[key] !== formData[key]
    );
  };

  const handleUpdateDevice = async () => {
    setIsSubmitting(true);
    try {
      const isValid = await validateForm();
      if (!isValid) {
        setIsSubmitting(false);
        setSuccessMessage('Please fill in all required fields before continuing.');
        setSuccessStatus('failed');
        setShowSuccessModal(true);
        return;
      }

      if (!CheckDeviceDetailsChanged()) {
        setIsSubmitting(false);
        setSuccessMessage("You haven't made any changes yet. Update a field and try again.");
        setShowSuccessModal(true);
        setSuccessStatus('failed');
        return;
      }

      // Prepare update data with proper image handling
      const updateData: UpdateDeviceRequest = {
        DeviceName: formData.DeviceName,
        DeviceLocationType: formData.DeviceLocationType,
        DeviceTypeId: formData.DeviceTypeId,
        SerialNumber: formData.SerialNumber,
        InstallationLocation: formData.InstallationLocation,
        Description: formData.Description,
        Latitude: formData.Latitude ?? undefined,
        Longitude: formData.Longitude ?? undefined,
        DeviceImage: formData.DeviceImage,
        DeviceImageUrl: formData.DeviceImageUrl,
        SiteId: formData.SiteId,
        // EnvironmentMode: formData.EnvironmentMode,
      };

      const response = await updateDevice(id as string, updateData);

      if (response?.success) {
        setSuccessMessage('Device details updated successfully.');
        setShowSuccessModal(true);
        setSuccessStatus('success');
        setModalActionText(undefined);
        setModalOnAction(undefined);

        // Redirect to dashboard after a short delay
        setTimeout(() => {
          setShowSuccessModal(false);
          if (isMobile && id) {
            navigate(`/devices/${id}`);
          } else {
            navigate('/devices');
          }
        }, 1500);
      } else {
        setSuccessMessage(
          response?.message || "We couldn't save your changes right now. Please try again in a moment."
        );
        setShowSuccessModal(true);
        setSuccessStatus('failed');
      }
    } catch (error) {
      setSuccessMessage('Something went wrong while saving your changes. Please try again.');
      setShowSuccessModal(true);
      setSuccessStatus('failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDevice = async () => {
    setIsSubmitting(true);
    try {
      const isValid = await validateForm();
      if (!isValid) {
        setIsSubmitting(false);
        setSuccessMessage('Please fill in all required fields before continuing.');
        setSuccessStatus('failed');
        setShowSuccessModal(true);
        setModalActionText('OK');
        setModalOnAction(() => {
          return () => {
            setShowSuccessModal(false);
            setModalActionText(undefined);
            setModalOnAction(undefined);
          };
        });
        return;
      }

      // If device exists (found via QR scan), update it instead of creating
      if (deviceExists && existingDeviceId) {
        // Get current user ID
        const userDataString = localStorage.getItem('userData');
        let currentUserId: number | undefined;
        if (userDataString) {
          try {
            const userData = JSON.parse(userDataString);
            currentUserId = userData.userId;
          } catch (error) {
            console.error('Error parsing user data:', error);
          }
        }

        // Prepare update data
        const updateData: UpdateDeviceRequest & { UserId?: number; OnboardingStatus?: string; OperationalStatus?: string } = {
          DeviceName: formData.DeviceName,
          DeviceTypeId: formData.DeviceTypeId,
          SerialNumber: formData.SerialNumber,
          InstallationLocation: formData.InstallationLocation,
          Description: formData.Description,
          Latitude: formData.Latitude ?? undefined,
          Longitude: formData.Longitude ?? undefined,
          DeviceImage: formData.DeviceImage,
          DeviceImageUrl: formData.DeviceImageUrl,
          // Add userId and status fields for devices with null userId
          UserId: currentUserId,
          OnboardingStatus: 'REGISTERED',
          OperationalStatus: 'UNKNOWN',
        };

        const response = await updateDevice(existingDeviceId.toString(), updateData);

        if (response?.success) {
          // Mark device as registered (already exists, so it's registered)
          setIsDeviceRegistered(true);
          setShowSuccessModal(true);
          setSuccessMessage('Your device details have been saved.');
          setSuccessStatus('success');
          setModalActionText("Proceed to Wi-Fi Connectivity");
          // setModalOnAction takes a functional updater, so the outer arrow
          // returns the handler — without the inner `return () => …` the
          // handler runs immediately and closes the modal we just opened.
          setModalOnAction(() => () => {
            setShowSuccessModal(false);
            setModalActionText(undefined);
            setModalOnAction(undefined);
            if (isMobile && cameFromQRScan && existingDeviceId) {
              setDeviceSavedSuccessfully(true);
            }
            setShowConfigFlow(true);
          });
        } else {
          setSuccessMessage(
            response?.message || 'Failed to update device. Please try again.'
          );
          setShowSuccessModal(true);
          setSuccessStatus('failed');
        }
        setIsSubmitting(false);
        return;
      }

      // New device - create it
      const response = await createDevice(formData);

      if (response?.success) {
        // Mark device as registered
        setIsDeviceRegistered(true);
        if (response.deviceDetails?.deviceId) {
          setExistingDeviceId(response.deviceDetails.deviceId);
        }
        setSuccessMessage(response?.message || 'Your device has been added successfully.');
        setShowSuccessModal(true);
        setSuccessStatus('success');
        setModalActionText("Proceed to Wi-Fi connectivity");
        // setModalOnAction takes a functional updater — return the handler
        // instead of executing it, otherwise the modal closes immediately.
        setModalOnAction(() => () => {
          setShowSuccessModal(false);
          setModalActionText(undefined);
          setModalOnAction(undefined);
          if (isMobile && cameFromQRScan) {
            setDeviceSavedSuccessfully(true);
          }
          setShowConfigFlow(true);
        });
        // Device created successfully - fetch device details to get WiFi status
        try {
          const deviceDetails = await deviceApi.getDeviceBySerialNumber(formData.SerialNumber.trim());
          const deviceOnboardingStatus = (deviceDetails as any).onboarding_status || deviceDetails.onboardingStatus;
          if (deviceOnboardingStatus !== undefined && deviceOnboardingStatus !== null) {
            const isWifiConnected = deviceOnboardingStatus?.toUpperCase() !== 'REGISTERED';
            setWifiConnected(isWifiConnected);
            
            // Get WiFi network name
            const networkName = deviceDetails.wifiNetworkName;
            
            if (isWifiConnected && networkName) {
              setWifiNetworkName(networkName);
            } else {
              setWifiNetworkName(null);
            }
          } else {
            setWifiConnected(false);
            setWifiNetworkName(null);
          }
        } catch (error) {
          console.error('Error fetching device WiFi status after registration:', error);
          setWifiConnected(false);
          setWifiNetworkName(null);
        }

        if (cameFromQRScan) {
          setDeviceSavedSuccessfully(true);
        }
      } else {
        // Check if device was registered but not connected
        if (response?.eventCode === 'DEVICE_NOT_CONNECTED') {
          // Set WiFi status to not connected
          setWifiConnected(false);
          setWifiNetworkName(null);
          // Show info prompt instead of error
          setSuccessMessage('The device has already been added, but Wi-Fi is not connected. Please proceed to connect Wi-Fi.');
          setSuccessStatus('success'); // Use success status for info prompt
          setShowSuccessModal(true);
          setModalActionText("Proceed to Wi-Fi connectivity");
          setModalOnAction(() => {
            return () => {
              setShowSuccessModal(false);
              setModalActionText(undefined);
              setModalOnAction(undefined);
              // Proceed to WiFi connection flow
              if (isMobile && cameFromQRScan) {
                setDeviceSavedSuccessfully(true);
              }
              setShowConfigFlow(true);
            };
          });
        } else {
          // Regular error handling
          const errorMsg = response?.message || response?.error || 'Failed to create device. Please try again.';
          setSuccessMessage(errorMsg);
          setShowSuccessModal(true);
          setSuccessStatus('failed');
          setModalActionText(undefined);
          setModalOnAction(undefined);
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred while creating the device.';
      
      // Check if it's an authentication error
      if (errorMessage.includes('not authenticated') || errorMessage.includes('log in')) {
        setSuccessMessage('Session expired. Please log in again.');
        setShowSuccessModal(true);
        setSuccessStatus('failed');
        
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setSuccessMessage(errorMessage || 'Failed to create device. Please try again.');
        setShowSuccessModal(true);
        setSuccessStatus('failed');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isMobile && id) {
      navigate(`/devices/${id}`);
    } else {
      navigate('/devices');
    }
  };

  const handleModalClose = () => {
    setShowSuccessModal(false);
    setModalActionText(undefined);
    setModalOnAction(undefined);
    // Only navigate if NOT showing config flow and NOT already handled by setTimeout
    // Config flow will handle navigation after completion
    // setTimeout navigation is already handled, so we don't need to navigate here
    // This prevents double navigation
  };

  const handleLocationSelect = (lat: number, lng: number) => {
    setFormData(prev => ({
      ...prev,
      Latitude: lat,
      Longitude: lng,
    }));

    // Clear location errors
    if (fieldErrors.Latitude || fieldErrors.Longitude) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.Latitude;
        delete newErrors.Longitude;
        return newErrors;
      });
    }
  };

  // ArcGIS World Geocoding endpoint (no API key required for display-only geocoding,
  // same service used by the map's Search widget) — used for the mobile search field.
  const GEOCODE_BASE = 'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer';

  // Fetch address suggestions as the user types in the mobile location search
  const fetchLocationSuggestions = async (text: string) => {
    if (!text || text.trim().length < 3) {
      setLocationSuggestions([]);
      return;
    }
    try {
      const url = `${GEOCODE_BASE}/suggest?f=json&maxSuggestions=5&text=${encodeURIComponent(text)}`;
      const res = await fetch(url);
      const data = await res.json();
      setLocationSuggestions(
        (data.suggestions || []).map((s: any) => ({ text: s.text, magicKey: s.magicKey }))
      );
    } catch (error) {
      console.error('Location suggestion error:', error);
      setLocationSuggestions([]);
    }
  };

  // Resolve a chosen suggestion to coordinates and set it as the device location
  const selectLocationSuggestion = async (suggestion: { text: string; magicKey: string }) => {
    try {
      const url = `${GEOCODE_BASE}/findAddressCandidates?f=json&outFields=*&maxLocations=1&singleLine=${encodeURIComponent(
        suggestion.text
      )}&magicKey=${encodeURIComponent(suggestion.magicKey)}`;
      const res = await fetch(url);
      const data = await res.json();
      const candidate = data.candidates?.[0];
      if (candidate?.location) {
        // ArcGIS returns x = longitude, y = latitude
        handleLocationSelect(candidate.location.y, candidate.location.x);
      }
    } catch (error) {
      console.error('Geocoding error:', error);
    }
    setLocationQuery(suggestion.text);
    setLocationSuggestions([]);
  };

  // Use the device's current GPS location (mobile)
  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setSuccessMessage('Geolocation is not supported on this device.');
      setSuccessStatus('failed');
      setShowSuccessModal(true);
      return;
    }
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      async position => {
        const { latitude, longitude } = position.coords;
        handleLocationSelect(latitude, longitude);
        setLocationSuggestions([]);

        // Reverse-geocode to get a human-readable location name
        try {
          const url = `${GEOCODE_BASE}/reverseGeocode?f=json&location=${longitude},${latitude}`;
          const res = await fetch(url);
          const data = await res.json();
          const addr = data?.address;
          const parts: string[] = [];
          const district = addr?.District || addr?.City || addr?.Neighborhood;
          if (district) parts.push(district);
          const metro = addr?.MetroArea;
          if (metro && metro !== district) {
            parts.push(metro);
          } else if (addr?.Subregion && addr?.Subregion !== district) {
            parts.push(addr.Subregion);
          }
          const name = parts.length > 0 ? parts.join(', ') : addr?.Match_addr || 'Current location';
          setLocationQuery(name);
        } catch {
          setLocationQuery('Current location');
        }

        setIsLocating(false);
      },
      error => {
        console.log('Location access denied or unavailable:', error.message);
        // Ask the native shell to open settings if running inside the RN WebView
        if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(JSON.stringify({ action: 'OPEN_APP_SETTINGS' }));
        }
        setSuccessMessage('Unable to access your location. Please enable location permissions and try again.');
        setSuccessStatus('failed');
        setShowSuccessModal(true);
        setIsLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const handleDeviceTypeChange = (deviceTypeId: string) => {
    const selectedOption = deviceTypes.find(
      option => option.id.toString() === deviceTypeId
    );
    if (selectedOption) {
      setFormData(prev => ({
        ...prev,
        DeviceTypeId: selectedOption.id,
      }));

      // Clear device type error
      if (fieldErrors.DeviceTypeId) {
        setFieldErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.DeviceTypeId;
          return newErrors;
        });
      }
    }
  };

  const handleLocationTypeChange = (locationType: string) => {
    if (locationType === '') {
      // Reset to default - clear everything
      setShowCustomLocationInput(false);
      setSelectedLocationType('');
      setFormData(prev => ({
        ...prev,
        InstallationLocation: '',
      }));
    } else if (locationType === 'Other') {
      setShowCustomLocationInput(true);
      setSelectedLocationType('Other');
      setFormData(prev => ({
        ...prev,
        InstallationLocation: '',
      }));

      // Close modal on mobile
      if (isMobile) {
        setShowLocationModal(false);
      }

      // Focus the custom location input after it appears
      setTimeout(() => {
        const customInput = document.querySelector(
          '#customLocationInput'
        ) as HTMLInputElement;
        if (customInput) {
          customInput.focus();
        }
      }, 100);
    } else {
      setShowCustomLocationInput(false);
      setSelectedLocationType(locationType);
      setFormData(prev => ({
        ...prev,
        InstallationLocation: locationType,
      }));

      // Close modal on mobile
      if (isMobile) {
        setShowLocationModal(false);
      }
    }

    // Clear installation location error
    if (fieldErrors.InstallationLocation) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.InstallationLocation;
        return newErrors;
      });
    }
  };

  // Handle site selection change 
  const handleSiteSelection = (siteId: number) => {
    setFormData(prev => ({ ...prev, SiteId: siteId }));
    setShowSiteModal(false);

    // Clear site error
    if (fieldErrors.SiteId) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.SiteId;
        return newErrors;
      });
    }
  }

  // const handleEnvironmentModeChange = (environmentMode: string) => {

  //   setFormData(prev => ({
  //     ...prev,
  //     EnvironmentMode: environmentMode,
  //   }));
  // };

  // const handleImageChange = (file: File | null) => {
  //   if (file) {
  //     setFormData(prev => ({
  //       ...prev,
  //       DeviceImage: file,
  //       DeviceImageUrl: URL.createObjectURL(file),
  //     }));
  //   } else {
  //     setFormData(prev => ({
  //       ...prev,
  //       DeviceImage: null,
  //       DeviceImageUrl: null,
  //     }));
  //   }
  //   if (fieldErrors.DeviceImage) {
  //     setFieldErrors(prev => {
  //       const newErrors = { ...prev };
  //       delete newErrors.DeviceImage;
  //       return newErrors;
  //     });
  //   }
  // };

  // const removeLocationSummary = () => {
  //   setFormData(prev => ({
  //     ...prev,
  //     InstallationLocation: '',
  //     // Sector: '',
  //   }));

  //   // Clear location-related errors
  //   if (fieldErrors.InstallationLocation) {
  //     setFieldErrors(prev => {
  //       const newErrors = { ...prev };
  //       delete newErrors.InstallationLocation;
  //       // delete newErrors.Sector;
  //       return newErrors;
  //     });
  //   }
  // };

  // const getLocationFromCoordinates = async (lat: number, lng: number) => {
  //   try {
  //     // Using OpenStreetMap Nominatim API for reverse geocoding
  //     const response = await fetch(
  //       `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10`
  //     );
  //     const data = await response.json();

  //     if (data.display_name) {
  //       // Extract city/area name from the full address
  //       const addressParts = data.display_name.split(', ');
  //       const cityName = addressParts[1] || addressParts[0]; // Usually city is the second part

  //       setFormData(prev => ({
  //         ...prev,
  //         InstallationLocation: cityName || prev.InstallationLocation,
  //       }));
  //     }
  //   } catch (error) {
  //     console.log('Could not fetch location name:', error);
  //   }
  // };
  // interface WifiNetwork {
  //   BSSID: string;
  //   SSID: string;
  //   capabilities: string;
  //   frequency: number;
  //   level: number;
  //   timestamp: number;
  // }

  // const [wifiNetworks, setWifiNetworks] = useState<WifiNetwork[]>([]);

  // interface WifiScanMessage {
  //   action: 'WIFI_SCAN_RESULT';
  //   networks: [];
  // }

  // async function sendWifiCredentials() {
  //   // const apiUrl = "http://192.168.4.1/wifi";
  //   const apiUrl="http://192.168.4.1/wifi";
  //   const payload = {
  //     ssid: wifiname,
  //     password: wifipassword,
  //     deviceid: formData?.SerialNumber,
  //     offsets: [
  //       { param: "PM2.5", x: 1, y: 0 },
  //       { param: "PM10", x: 1, y: 0 }
  //     ]      
  //   };
  
  //   try {
  //     const response = await fetch(apiUrl, {
  //       method: "POST",
  //       headers: {
  //         "Content-Type": "application/json"
  //       },
  //       body: JSON.stringify(payload)
  //     });
  
  //     const result = await response.json();
  //     console.log("API Response:", result);
  
  //   } catch (error) {
  //     console.error("Error sending WiFi data:", error);
  //   }
  // }
  

  // useEffect(() => {
  //   const handler = (event: MessageEvent) => {
  //     try {
  //       console.log("Web received:", event.data);
  
  //       const data = JSON.parse(event.data) as WifiScanMessage;
  
  //       if (data.action === "WIFI_SCAN_RESULT") {
  //         setWifiNetworks(data.networks);
  //       }
  //     } catch (e) {
  //       console.error("Invalid message from RN:", e);
  //     }
  //   };
  
  //   // 🔥 React Native WebView sends messages through BOTH
  //   window.addEventListener("message", handler);
  //   // @ts-ignore - RN dispatches custom event
  //   document.addEventListener("message", handler);
  
  //   return () => {
  //     window.removeEventListener("message", handler);
  //     // @ts-ignore
  //     document.removeEventListener("message", handler);
  //   };
  // }, []);


  return (
    <DashboardLayout>
      {showConfigFlow && !isRNApp() && !isMobile && formData.SerialNumber ? (
        <DeviceConfigFlowWeb
          setShowConfigFlow={setShowConfigFlow}
          SerialNumber={formData.SerialNumber}
          deviceId={existingDeviceId?.toString() ?? id ?? ''}
        />
      ) : (
        <>
      <div className={`manually-add-device-container scrollbar ${isMobile && (showConfigFlow || showAddSiteForm) ? 'isMobileModalOpen' : ''} `}>
        {/* Header */}
        <div className="manually-add-device-header zoomOutLevel">
          <div className="add-device-header-title">
            <button
              onClick={() => {
                if (isMobile && id) {
                  navigate(`/devices/${id}`);
                } else {
                  navigate('/devices');
                }
              }}
              className="back-button zoomOutLevelForDeviceInfoCard"
            >
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                className="back-icon"
              >
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
              </svg>
            </button>
            <h3 className="device-title">
              {id ? 'Edit Device' : 'Add Device'}
            </h3>
          </div>
          <p className="device-subtitle manually-add-device-subtitle ml-12">
            Add a new SmartEco device to your dashboard
          </p>
        </div>

        <div className="add-device-form-content">
          {/* Form Content */}
          <form className="scrollbar zoomOutLevel">
            <div className="add-device-form-section">
              {/* Basic Information */}
              <div className="add-device-form-group">
                <h3 className="add-device-section-title">Basic Information</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div className="add-device-input-group">
                    <label htmlFor="deviceName">Device Name <span className="text-red-500"> *</span></label>
                    <input
                      type="text"
                      id="deviceName"
                      value={formData.DeviceName}
                      onChange={e => {
                        // Filter out special characters - only allow alphanumeric and spaces
                        const filteredValue = e.target.value
                          .replace(/[^a-zA-Z0-9\s]/g, '')
                          .replace(/^\s+/, '')
                          .replace(/\s{2,}/g, ' ');
                        // Limit to 30 characters
                        const limitedValue = filteredValue.slice(0, 30);
                        handleInputChange('DeviceName', limitedValue);
                      }}
                      placeholder="Enter device name"
                      minLength={4}
                      maxLength={30}
                      className={
                        fieldErrors.DeviceName
                          ? 'add-device-input-error-border'
                          : ''
                      }
                    />
                    <p className="add-device-input-group-description">
                      Provide a unique name for this device
                    </p>
                    {fieldErrors.DeviceName && (
                      <p className="add-device-input-error">
                        <AlertCircle className="add-device-error-icon" />
                        {fieldErrors.DeviceName}
                      </p>
                    )}
                  </div>

                  {/* Installation Location — selectable icon chips */}
                  <div className="add-device-input-group">
                    <label>Installation Location <span className="text-red-500"> *</span></label>
                    <div className="add-device-location-chips scrollbar">
                      {locationOptions.map(option => {
                        const Icon = LOCATION_ICONS[option] || MapPin;
                        const isSelected = selectedLocationType === option;
                        return (
                          <button
                            type="button"
                            key={option}
                            className={`add-device-location-chip ${isSelected ? 'selected' : ''} ${
                              fieldErrors.InstallationLocation ? 'add-device-location-chip-error' : ''
                            }`}
                            onClick={() => handleLocationTypeChange(option)}
                          >
                            <Icon className="add-device-location-chip-icon" />
                            <span>{option}</span>
                          </button>
                        );
                      })}
                    </div>
                    {showCustomLocationInput && (
                      <div className="add-device-location-custom-input">
                        <input
                          type="text"
                          id="customLocationInput"
                          value={formData.InstallationLocation}
                          onChange={e =>
                            handleInputChange('InstallationLocation', e.target.value)
                          }
                          placeholder="Please enter your location (e.g. 'Downtown office')"
                          className={
                            fieldErrors.InstallationLocation
                              ? 'add-device-input-error-border'
                              : ''
                          }
                        />
                      </div>
                    )}
                    <p className="add-device-input-group-description">
                      {showCustomLocationInput
                        ? 'Enter a custom location name for the device'
                        : 'Select the location where device is installed'}
                    </p>
                    {fieldErrors.InstallationLocation && (
                      <p className="add-device-input-error">
                        <AlertCircle className="add-device-error-icon" />
                        {fieldErrors.InstallationLocation}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Other Details — collapsible section, minimized by default */}
              <div className="add-device-form-group">
                <button
                  type="button"
                  className="other-details-toggle"
                  onClick={() => setShowOtherDetails(prev => !prev)}
                  aria-expanded={showOtherDetails}
                >
                  <h3 className="add-device-section-title">
                    Other Details
                  </h3>
                  <ChevronDown
                    className={`h-5 w-5 text-[var(--color-text)] transition-transform ${showOtherDetails ? 'rotate-180' : ''}`}
                  />
                </button>
                {showOtherDetails && (
                <>
                <div className="grid grid-cols-1 gap-4">
                  <div className="add-device-input-group">
                    <label htmlFor="serialNumber">Serial Number<span className="text-red-500"> *</span></label>
                    <input
                      type="text"
                      id="serialNumber"
                      value={formData.SerialNumber}
                      readOnly
                      disabled={true}
                      placeholder="Enter serial number"
                      className={`uppercase ${fieldErrors.SerialNumber ? 'add-device-input-error-border' : ''}`}
                    />
                   
                    {fieldErrors.SerialNumber && (
                      <p className="add-device-input-error">
                        <AlertCircle className="add-device-error-icon" />
                        {fieldErrors.SerialNumber}
                      </p>
                    )}
                  </div>
                  {/* <button
                    type="button"
                    className="add-device-upload-btn"
                    onClick={() => {
                      window.ReactNativeWebView?.postMessage(
                        JSON.stringify({ action: 'SCAN_WIFI' })
                      );
                    }}
                  >
                    Scan Device Network
                  </button> */}
                  {/* <div>
                    <select>
                      {wifiNetworks.filter(network => network.SSID.startsWith('IAQ_'))
                      .map((network, index) => (
                        <option key={index} value={index}>
                          {network.SSID}
                        </option>
                      ))}
                    </select>
                    <label htmlFor="wifiname">Wifi Name</label>
                    <input
                      type="text"
                      id="wifiname"
                      value={wifiname}
                      onChange={e =>
                        setWifiName(e.target.value)
                      }
                      placeholder="Enter Wifi Name"
                    />
                    <label htmlFor="wifipassword">Wifi Password</label>
                    <input
                      type="password"
                      id="wifipassword"
                      value={wifipassword}
                      onChange={e =>
                        setWifiPassword(e.target.value)
                      }
                      placeholder="Enter Wifi password"
                    />
                  </div> */}
                  {/* <button
                    type="button"
                    className="add-device-upload-btn"
                    onClick={() => {
                      sendWifiCredentials();
                    }}
                  >
                    Config Device
                  </button> */}
                  <div className="add-device-input-group">
                    <label htmlFor="deviceModel">Device Model</label>
                    <input
                      type="text"
                      id="deviceModel"
                      value={formData.DeviceModel}
                      readOnly
                      disabled
                      placeholder="Auto-filled from serial number"
                      className="add-device-input"
                      style={{ cursor: 'not-allowed' }}
                    />
                  </div>
                  
                  <div className="add-device-input-group">
                    <label htmlFor="deviceType">Device Type</label>
                    <div className="relative">
                      <select
                        id="deviceType"
                        value={formData.DeviceTypeId}
                        disabled={true}
                        onChange={e => handleDeviceTypeChange(e.target.value)}
                        className={
                          fieldErrors.DeviceTypeId
                            ? 'add-device-input-error-border'
                            : ''
                        }
                        style={{fontSize: formData.DeviceTypeId === 0 ? '0.875rem' : '', fontWeight: formData.DeviceTypeId === 0 ? '300' : ''}}
                      >
                        <option value="">
                          {deviceTypesLoading
                            ? 'Loading device types...'
                            : 'Select device type'}
                        </option>
                        {deviceTypes.map(option => (
                          <option key={option.id} value={option.id.toString()}>
                            {option.description}
                          </option>
                        ))}
                      </select>
                      {/* <ChevronDown className="h-5 w-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-800" /> */}
                    </div>
                    {deviceTypesError && (
                      <p className="add-device-input-error">
                        <AlertCircle className="add-device-error-icon" />
                        {deviceTypesError}
                      </p>
                    )}
                    {fieldErrors.DeviceTypeId && (
                      <p className="add-device-input-error">
                        <AlertCircle className="add-device-error-icon" />
                        {fieldErrors.DeviceTypeId}
                      </p>
                    )}
                  </div>

                   
                  {/* WiFi Network Status Field - Only show after device is registered or when editing */}
                  {(isDeviceRegistered || id) && wifiConnected !== null && (
                    <div className="add-device-input-group">
                      <label htmlFor="wifiNetwork">Wi-Fi Network</label>
                      <input
                        type="text"
                        id="wifiNetwork"
                        value={wifiConnected && wifiNetworkName ? wifiNetworkName : 'Not connected'}
                        readOnly
                        disabled
                        className="add-device-input"
                        style={{ 
                          cursor: 'not-allowed'
                        }}
                      />
                    </div>
                  )}

                  {/* <div className="add-device-input-group">
                    <label htmlFor="environmentMode">Environment Mode</label>
                    <div className="relative">
                      <select
                        id="environmentMode"
                        value={formData.EnvironmentMode || ''}
                        onChange={e => handleEnvironmentModeChange(e.target.value)}
                        className={
                          fieldErrors.EnvironmentMode
                            ? 'add-device-input-error-border'
                            : ''
                        }
                      >
                        <option value="">Select environment mode</option>
                        {EnvironmentModes.map(option => (
                          <option key={option.id} value={option.mode}>
                            {option.mode}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="h-5 w-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-800" />
                    </div>
                  </div> */}

                  <div className="add-device-radio-group">
                    <label>Installation Type</label>
                    <div className="add-device-radio-options">
                      <label className="add-device-radio-option">
                        <input
                          type="radio"
                          name="deviceLocationType"
                          value="indoor"
                          checked={formData.DeviceLocationType === 'indoor'}
                          onChange={() =>
                            handleDeviceLocationTypeChange('indoor')
                          }
                        />
                        <div className="add-device-radio-option-content flex items-center gap-2">
                          <span className="add-device-radio-custom"></span>
                          <p>Indoor</p>
                        </div>
                      </label>
                      <label className="add-device-radio-option">
                        <input
                          type="radio"
                          name="deviceLocationType"
                          value="outdoor"
                          checked={formData.DeviceLocationType === 'outdoor'}
                          onChange={() =>
                            handleDeviceLocationTypeChange('outdoor')
                          }
                        />
                        <div className="add-device-radio-option-content flex items-center gap-2">
                          <span className="add-device-radio-custom"></span>
                          <p>Outdoor</p>
                        </div>
                      </label>
                    </div>
                    {fieldErrors.DeviceLocationType && (
                      <p className="add-device-input-error">
                        <AlertCircle className="add-device-error-icon" />
                        {fieldErrors.DeviceLocationType}
                      </p>
                    )}
                  </div>

                  {/* Device Status - Commented out for now, will be uncommented if required in future */}
                  {/* <div className="add-device-radio-group">
                  <label>Device Status</label>
                  <div className="add-device-radio-options">
                    <label className="add-device-radio-option">
                      <input
                        type="radio"
                        name="isActive"
                        value="true"
                        checked={formData.IsActive === true}
                        onChange={() => handleInputChange('IsActive', true)}
                      />
                      <div className="add-device-radio-option-content flex items-center gap-2">
                        <span className="add-device-radio-custom"></span>
                        <p>Active</p>
                      </div>
                    </label>
                    <label className="add-device-option">
                      <input
                        type="radio"
                        name="isActive"
                        value="false"
                        checked={formData.IsActive === false}
                        onChange={() => handleInputChange('IsActive', false)}
                      />
                      <div className="add-device-radio-option-content flex items-center gap-2">
                        <span className="add-device-radio-custom"></span>
                        <p>Inactive</p>
                      </div>
                    </label>
                  </div>
                </div> */}
                </div>

                {/* Location */}
                <div className="add-device-form-group">
                {/* <div className="flex justify-between items-center mb-4"> */}
                {/* <h3 className="add-device-section-title">Installation Location <span className="text-red-500">*</span></h3> */}
                {/* <button
                    type="button"
                    className="add-device-add-other-btn"
                    onClick={() => handleSectorChange('custom')}
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                    </svg>
                    Add Other
                  </button>
                </div> */}
                {/* Location Details */}
                <div className="flex flex-col gap-4">
                  {/* Site — Viewers and individual users can't manage sites */}
                  {canManageSites && (
                  <div className="add-device-input-group">
                    <label htmlFor="siteId">Site <span className="text-red-500"> *</span></label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch' }}>
                      <div style={{ flex: 1 }}>
                        {isMobile ? (
                          <div className="relative">
                            <button
                              type="button"
                              className={`mobile-location-dropdown-btn ${fieldErrors.SiteId ? 'add-device-input-error-border' : ''} ${formData.SiteId ? 'location-selected' : ''}`}
                              onClick={() => setShowSiteModal(true)}
                            >
                              <div className="flex items-center justify-between">
                                <div className='flex items-center gap-1'>
                                  {formData.SiteId ? (
                                    <>
                                      <span>{sites.find(s => s.siteId === formData.SiteId)?.siteName}</span>
                                      <span>{`(${sites.find(s => s.siteId === formData.SiteId)?.siteLocation})`}</span>
                                    </>
                                  ) : (
                                    <span>Select Site</span>
                                  )}
                                </div>
                                <ChevronDown className="h-5 w-5 text-[var(--color-text)]" />
                              </div>
                            </button>
                          </div>
                        ) : (
                          <div className="relative">
                            <select
                              id="siteId"
                              value={formData.SiteId ?? 0}
                              onChange={e => handleSiteSelection(Number(e.target.value))}
                              className={fieldErrors.SiteId ? 'add-device-input-error-border' : ''}
                            >
                              <option value={0}>Select Site</option>
                              {sites.map(option => (
                                <option key={option.siteId} value={option.siteId}>
                                  {option.siteName} {`(${option.siteLocation})`}
                                </option>
                              ))}
                            </select>
                            <ChevronDown className="h-5 w-5 absolute right-3 top-1/2 -translate-y-1/2 text-[var(--color-text)]" />
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="add-site-btn"
                        style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                        onClick={() => { setShowAddSiteForm(true); setNewSiteName(''); setNewSiteLocation(''); }}
                      >
                        <Plus size={16} /> Add Site
                      </button>
                    </div>

                    {fieldErrors.SiteId && (
                      <p className="add-device-input-error">
                        <AlertCircle className="add-device-error-icon" />
                        {fieldErrors.SiteId}
                      </p>
                    )}
                  </div>
                  )}

                  {/* <div className="add-device-input-group">
                    <label htmlFor="sector">Sector or Group</label>
                    <div className="relative">
                      <select
                        id="sector"
                        value={formData.Sector}
                        onChange={e => handleSectorChange(e.target.value)}
                        className={
                          fieldErrors.Sector
                            ? 'add-device-input-error-border'
                            : ''
                        }
                      >
                        <option value="">Select sector or group</option>
                        {sectorOptions.map(option => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                        <option value="custom">Add Other</option>
                      </select>
                      <ChevronDown className="h-5 w-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-800" />
                    </div>
                    {fieldErrors.Sector && (
                      <p className="add-device-input-error">
                        <AlertCircle className="add-device-error-icon" />
                        {fieldErrors.Sector}
                      </p>
                    )}
                  </div> 

                  {showCustomSectorInput && (
                    <form
                      className="add-device-custom-sector-input w-full"
                      onSubmit={handleCustomSectorSubmit}
                    >
                      <input
                        type="text"
                        placeholder="Enter custom sector"
                        value={customSector}
                        onChange={e => {
                          setCustomSector(e.target.value);
                          // Clear sector error when typing
                          if (fieldErrors.Sector) {
                            setFieldErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors.Sector;
                              return newErrors;
                            });
                          }

                          // Validate custom sector length in real-time
                          if (
                            e.target.value.trim().length > 0 &&
                            e.target.value.trim().length < 3
                          ) {
                            setFieldErrors(prev => ({
                              ...prev,
                              Sector:
                                'Custom sector must be at least 3 characters',
                            }));
                          }
                        }}
                        onKeyPress={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleCustomSectorSubmit(e);
                          }
                        }}
                        onBlur={() => {
                          // Validate custom sector on blur
                          if (
                            customSector.trim().length > 0 &&
                            customSector.trim().length < 3
                          ) {
                            setFieldErrors(prev => ({
                              ...prev,
                              Sector:
                                'Custom sector must be at least 3 characters',
                            }));
                          }
                        }}
                        className={
                          fieldErrors.Sector
                            ? 'add-device-input-error-border'
                            : ''
                        }
                      />
                      <button
                        type="submit"
                        className="add-device-submit-custom-btn"
                        onClick={e => {
                          e.preventDefault();
                          handleCustomSectorSubmit(e);
                        }}
                      >
                        Add
                      </button>
                    </form>
                  )} */}

                  {/* Location Summary */}
                  {/* {formData.InstallationLocation && (
                    <div className="add-device-location-summary md:col-span-2">
                      <div className="add-device-summary-content">
                        {formData.InstallationLocation && (
                          <span className="add-device-summary-item">
                            Site Name :{' '}
                            <span className="location-name">
                              {formData.InstallationLocation}
                            </span>
                          </span>
                        )}
                        {/* {formData.Sector && (
                          <span className="add-device-summary-item">
                            Sector or Group :{' '}
                            <span className="sector-name">
                              {formData.Sector}
                            </span>
                          </span>
                        )} 
                      </div>
                      <button
                        type="button"
                        className="add-device-remove-location-btn"
                        onClick={removeLocationSummary}
                        title="Remove location"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="lucide lucide-circle-x-icon lucide-circle-x"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <path d="m15 9-6 6" />
                          <path d="m9 9 6 6" />
                        </svg>
                      </button>
                    </div>
                  )} */}
                  {/* Mobile view: search + current location (map removed on mobile) */}
                  {isMobile && (
                    <div className="add-device-input-group mt-4">
                      <label htmlFor="locationSearch">Search Location</label>
                      <div className="location-search-wrapper">
                        <Search className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text)]" />
                        <input
                          type="text"
                          id="locationSearch"
                          className="location-search-input"
                          value={locationQuery}
                          onChange={e => {
                            setLocationQuery(e.target.value);
                            fetchLocationSuggestions(e.target.value);
                          }}
                          placeholder="Search by location"
                          autoComplete="off"
                        />
                        {locationSuggestions.length > 0 && (
                          <ul className="location-search-suggestions">
                            {locationSuggestions.map(s => (
                              <li
                                key={s.magicKey}
                                className="location-search-suggestion"
                                onClick={() => selectLocationSuggestion(s)}
                              >
                                {s.text}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                      <button
                        type="button"
                        className="use-current-location-btn"
                        onClick={handleUseCurrentLocation}
                        disabled={isLocating}
                      >
                        <MapPin className="h-4 w-4" />
                        {isLocating ? 'Getting location…' : 'Use Current Location'}
                      </button>

                      {(fieldErrors.Latitude || fieldErrors.Longitude) && (
                        <p className="add-device-input-error mt-2">
                          <AlertCircle className="add-device-error-icon" />
                          {fieldErrors.Latitude || fieldErrors.Longitude}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
                </>
                )}
              </div>

              {/* Additional Details section hidden */}
            </div>
          </form>
          {/* Location Map */}
          {!isMobile && (
            <div className="arcgis-map-container">
              <ArcGISMap
                latitude={formData.Latitude ?? 0}
                longitude={formData.Longitude ?? 0}
                onLocationSelect={(lat, lng) => {
                  handleLocationSelect(lat, lng);
                  // Try to get location name from coordinates (reverse geocoding)
                  // getLocationFromCoordinates(lat, lng);
                }}
              />
              {(fieldErrors.Latitude || fieldErrors.Longitude) && (
                <p className="add-device-input-error mt-2">
                  <AlertCircle className="add-device-error-icon" />
                  {fieldErrors.Latitude || fieldErrors.Longitude}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="add-device-action-buttons zoomOutLevel">
          <button
            onClick={handleCancel}
            className="add-device-cancel-btn"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={id ? handleUpdateDevice : handleSaveDevice}
            className="add-device-save-btn"
            disabled={isSubmitting}
          >
            {id ? 'Update' : 'Next'}
          </button>
        </div>
      </div>

      {/* Mobile Location Selection Modal */}
      {isMobile && showLocationModal && (
        <div
          className="mobile-modal-overlay"
          onClick={() => setShowLocationModal(false)}
        >
          <div className="mobile-modal" onClick={e => e.stopPropagation()}>
            <div className="mobile-modal-header">
              <h3 className="mobile-modal-title">Select Location</h3>
              <X
                className="w-5 h-5 text-[var(--color-text)] cursor-pointer"
                onClick={() => setShowLocationModal(false)}
              />
            </div>
            <div className="mobile-modal-content">
              {locationOptions.map(option => (
                <div
                  key={option}
                  className="mobile-modal-option cursor-pointer"
                  onClick={() => handleLocationTypeChange(option)}
                >
                  <input
                    type="radio"
                    name="locationType"
                    checked={selectedLocationType === option}
                    onChange={() => handleLocationTypeChange(option)}
                    className="mobile-modal-radio"
                  />
                  <span>{option}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Site Selection Bottom Modal (mobile only) */}
      {canManageSites && showSiteModal && (
        <div
          className="mobile-modal-overlay"
          onClick={() => setShowSiteModal(false)}
        >
          <div className="mobile-modal" onClick={e => e.stopPropagation()}>
            <div className="mobile-modal-header">
              <h3 className="mobile-modal-title">Select Site</h3>
              <X
                className="w-5 h-5 text-[var(--color-text)] cursor-pointer"
                onClick={() => setShowSiteModal(false)}
              />
            </div>
            <div className="mobile-modal-content">
              {sites.map(s => (
                <div
                  key={s.siteId}
                  className="mobile-modal-option cursor-pointer"
                  onClick={() => handleSiteSelection(s.siteId)}
                >
                  <input
                    type="radio"
                    name="siteSelection"
                    checked={formData.SiteId === s.siteId}
                    onChange={() => handleSiteSelection(s.siteId)}
                    className="mobile-modal-radio"
                  />
                  <span>{s.siteName}</span>
                  <span>{`(${s.siteLocation})`}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Add New Site Popup — responsive: popup on web, fullscreen on mobile */}
      {canManageSites && showAddSiteForm && (
        <div
          className="modal-overlay zoomOutLevel"
          onClick={() => { setShowAddSiteForm(false); setNewSiteName(''); setNewSiteLocation(''); }}
        >
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="flex md:items-start items-center md:flex-col flex-row">
                <button
                  type="button"
                  onClick={() => { setShowAddSiteForm(false); setNewSiteName(''); setNewSiteLocation(''); }}
                  className="md:hidden block mr-3"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="back-icon">
                    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                  </svg>
                </button>
                <h2 className="modal-title">Add New Site</h2>
                <p className="modal-subtitle md:block hidden">Enter the site name and location.</p>
              </div>
              <button
                type="button"
                onClick={() => { setShowAddSiteForm(false); setNewSiteName(''); setNewSiteLocation(''); }}
                className="modal-close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="modal-form">
              <div className="form-group">
                <label className="form-label">Site Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter Site Name"
                  value={newSiteName}
                  onChange={e => setNewSiteName(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Site Location <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter Site Location"
                  value={newSiteLocation}
                  onChange={e => setNewSiteLocation(e.target.value)}
                />
              </div>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="modal-button secondary"
                onClick={() => { setShowAddSiteForm(false); setNewSiteName(''); setNewSiteLocation(''); }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="modal-button primary"
                onClick={async () => {
                  if (!newSiteName.trim() || !newSiteLocation.trim()) return;
                  const site = await createSite?.({ siteName: newSiteName.trim(), siteLocation: newSiteLocation.trim() });
                  if (site) {
                    setFormData(prev => ({ ...prev, SiteId: site.siteId }));
                    setNewSiteName('');
                    setNewSiteLocation('');
                    setShowAddSiteForm(false);
                  }
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleModalClose}
        title={(() => {
          if (successStatus === 'success') {
            return id ? 'Device Updated' : 'Device Added';
          }
          const msg = successMessage.toLowerCase();
          if (msg.includes("haven't made any changes") || msg.includes('no changes')) {
            return 'No Changes to Save';
          }
          if (msg.includes('required fields') || msg.includes('fill in all')) {
            return 'Missing Required Fields';
          }
          return id ? "Couldn't Update Device" : "Couldn't Add Device";
        })()}
        subtitle={successMessage}
        status={successStatus}
        actionText={modalActionText}
        onAction={modalOnAction}
      />

      {showConfigFlow && (isRNApp() || isMobile) && (
        <DeviceConfigFlowMobile setShowConfigFlow={setShowConfigFlow}  SerialNumber={formData.SerialNumber} deviceId={existingDeviceId?.toString() ?? id ?? ''}/>
      )}

      {/* Loading Overlay */}
      {isSubmitting && (
        <div className="fixed inset-0 bg-[var(--modal-overlay-background)] bg-opacity-70 flex items-center justify-center z-[100]">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
            <p className="text-white text-lg font-medium">
              {id ? 'Updating device...' : 'Creating device...'}
            </p>
          </div>
        </div>
      )}
        </>
      )}
    </DashboardLayout>
  );
};

export default ManuallyAddDevicePage;
